/**
 * Insights hub: "Request a Custom Report" — a deliberately light card (dashed,
 * muted) that opens a short form. Submissions land in report_requests with the
 * program / entity / year context attached so the request is self-describing.
 * Used by both the MIPS InsightsGrid and the APP Plus Insights page.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquarePlus, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { apiClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';

interface Props {
  program: 'mips' | 'app-plus';
  entityType?: 'practice' | 'aco';
  entityId?: number | string | null;
  entityLabel?: string | null;
  programYear?: number;
  className?: string;
}

interface MyRequest {
  id: number;
  title: string;
  status: string;
  created_at: string;
}

const STATUS_LABEL: Record<string, string> = {
  new: 'Received', in_review: 'In review', planned: 'Planned', delivered: 'Delivered', declined: 'Declined',
};

export function RequestCustomReportCard({ program, entityType, entityId, entityLabel, programYear, className }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [frequency, setFrequency] = useState('one_time');
  const [urgency, setUrgency] = useState('normal');
  const [saving, setSaving] = useState(false);

  const mineQ = useQuery<{ requests: MyRequest[] }>({
    queryKey: ['report-requests', 'mine'],
    queryFn: () => apiClient.get('/api/report-requests/mine'),
    staleTime: 60_000,
  });
  const mine = mineQ.data?.requests ?? [];
  const openCount = mine.filter(r => r.status === 'new' || r.status === 'in_review' || r.status === 'planned').length;

  const reset = () => { setTitle(''); setDescription(''); setFrequency('one_time'); setUrgency('normal'); };

  const submit = async () => {
    if (title.trim().length < 3 || description.trim().length < 10) {
      toast({ title: 'Add a little more detail', description: 'A short title and a few sentences on what you need.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await apiClient.post('/api/report-requests', {
        program, entityType, entityId: entityId ?? null, entityLabel: entityLabel ?? null, programYear,
        title: title.trim(), description: description.trim(), frequency, urgency,
      });
      toast({ title: 'Request sent', description: 'We will review it and follow up.' });
      reset();
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ['report-requests', 'mine'] });
    } catch (e) {
      toast({ title: 'Could not send request', description: e instanceof Error ? e.message : String(e), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'group flex min-h-[280px] flex-col rounded-lg border-2 border-dashed border-slate-300 bg-slate-50/50 p-6 text-left transition-colors hover:border-slate-400 hover:bg-slate-50',
          className,
        )}
      >
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-white text-slate-500 ring-1 ring-slate-200 group-hover:text-slate-800">
            <MessageSquarePlus className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold leading-tight text-slate-700">Request a Custom Report</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Don't see the view you need? Tell us what question you're trying to answer and we'll build it.
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-1 items-center justify-center">
          {mine.length > 0 ? (
            <div className="w-full space-y-1.5">
              {mine.slice(0, 3).map(r => (
                <div key={r.id} className="flex items-center justify-between gap-2 rounded-md bg-white/70 px-2.5 py-1.5 text-xs ring-1 ring-slate-200">
                  <span className="truncate text-slate-700">{r.title}</span>
                  <Badge variant={r.status === 'delivered' ? 'default' : 'secondary'} className="h-5 shrink-0 text-[10px]">
                    {STATUS_LABEL[r.status] ?? r.status}
                  </Badge>
                </div>
              ))}
              {mine.length > 3 && <p className="text-center text-[11px] text-muted-foreground">+{mine.length - 3} more</p>}
            </div>
          ) : (
            <p className="text-center text-xs text-muted-foreground">No requests yet</p>
          )}
        </div>
        <div className="mt-auto flex items-center justify-between border-t border-dashed pt-4 text-sm text-muted-foreground">
          <span>{openCount > 0 ? `${openCount} open request${openCount === 1 ? '' : 's'}` : 'Takes a minute'}</span>
          <span className="font-medium text-slate-700 group-hover:underline">New request →</span>
        </div>
      </button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Request a Custom Report</DialogTitle>
            <DialogDescription>
              {entityLabel ? <>For <span className="font-medium text-foreground">{entityLabel}</span>{programYear ? ` · PY${programYear}` : ''}. </> : null}
              Describe the question you want answered — measures, populations, time period, how you'd use it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="rr-title">Title</Label>
              <Input id="rr-title" value={title} onChange={e => setTitle(e.target.value)} maxLength={200}
                placeholder="e.g. Depression screening follow-up by clinician, monthly" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rr-desc">What are you looking for?</Label>
              <Textarea id="rr-desc" value={description} onChange={e => setDescription(e.target.value)} rows={5} maxLength={5000}
                placeholder="Which measures or populations, what comparison (TIN vs TIN, month over month…), who will use it and for what decision." />
              <p className="text-right text-[11px] text-muted-foreground">{description.length}/5000</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>How often</Label>
                <Select value={frequency} onValueChange={setFrequency}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_time">One time</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="ongoing">Ongoing (live page)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Urgency</Label>
                <Select value={urgency} onValueChange={setUrgency}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low — when you get to it</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High — needed for an upcoming decision</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving}>
              {saving ? 'Sending…' : <><CheckCircle2 className="mr-1.5 h-4 w-4" /> Send request</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
